import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BlockMultimediaContent,
  ProgressLessonContent,
} from 'store/states/lessons.state'
import { IMainState } from 'store/states'
import { useCourse, isMultimediaFinalized } from 'contexts'

import * as fromActionsProgress from 'store/actions/blockProgress.action'
import Loader, { LoaderSizes } from 'components/ui/loader/Loader'
import { useURL } from './hooks/useURL'
import styles from './Video.module.scss'
import { useInitialContentProgress } from './hooks/useInitialContentProgress'

interface VideoProps {
  id: number
  url: string
  onMobile: boolean
  data: BlockMultimediaContent
  courseId: number
}

export const Video: React.FC<VideoProps> = ({
  id,
  url: baseUrl,
  onMobile,
  data,
  courseId,
}) => {
  const { t } = useTranslation()
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [errorPlay, setErrorPlay] = React.useState(false)
  const dispatch = useDispatch()
  const { lockProgress, fileData, denyDownload } = data
  const { url, resolution, type } = useURL({ baseUrl, data })
  const maxProgress = useRef<number>(0)

  const videoProgress = useSelector(
    ({
      progressLessonContent,
    }: IMainState): ProgressLessonContent | undefined =>
      progressLessonContent.progress[id]
  )

  useMemo(() => {
    if (videoProgress)
      videoProgress.currentTime = Math.min(
        videoProgress.currentTime,
        videoProgress.duration
      )
  }, [videoProgress])

  const videoProgressRef = useRef(videoProgress)
  const { isLastLessonHigher, canAnswer, previewMode } = useCourse()

  const block = useMemo(
    () => ({
      id,
      courseId,
      blockInstanceableId: data.blockInstanceableId,
    }),
    [courseId, data.blockInstanceableId, id]
  )

  const dispatchedProgressRef = useRef<ProgressLessonContent>()
  const dispatchUpdate = useCallback(() => {
    const dispatched = dispatchedProgressRef.current
    const current = videoProgressRef.current
    if (dispatched && current)
      if (
        dispatched.currentTime === current.currentTime &&
        dispatched.duration === current.duration
      )
        return
    if (!dispatched) dispatchedProgressRef.current = current
    if (current && !previewMode) {
      dispatch({
        type: fromActionsProgress.SET_LESSON_BLOCKS_PROGRESS,
        data: {
          ...block,
          currentTime: current.currentTime,
          duration: current.duration,
        },
      })
      dispatchedProgressRef.current = current
    }
  }, [block, dispatch, previewMode])

  useEffect(() => {
    const interval = setInterval(() => dispatchUpdate(), 10000)
    return () => clearInterval(interval)
  }, [dispatchUpdate])

  const { loading: loadingProgress } = useInitialContentProgress({
    videoRef: videoRef,
    block,
  })

  useEffect(() => {
    videoProgressRef.current = videoProgress // save state value to ref
    if (videoProgress) {
      if (videoProgress.currentTime > maxProgress.current)
        maxProgress.current = videoProgress.currentTime
    }
  }, [videoProgress])

  const setProgressLessonContent = useCallback(
    (progress: number) => {
      dispatch(
        fromActionsProgress.Actions.currentLessonBlockProgressSucess({
          id: block.id,
          blockInstanceableId: block.blockInstanceableId,
          data: {
            duration: videoRef.current?.duration || NaN,
            currentTime: progress,
          },
        })
      )
    },
    [block, dispatch]
  )

  const isFinalized =
    !canAnswer ||
    (!!videoProgress && isMultimediaFinalized(data, videoProgress))

  const showAlert = lockProgress && !isLastLessonHigher && !isFinalized

  return (
    <>
      {showAlert && (
        <div className={classNames('mt-3', styles.root)}>
          <div className={classNames('w-100', styles.pending)}>
            <div className="px-4 row no-gutters py-1">
              <div className="col-auto d-flex align-items-center">
                <FontAwesomeIcon icon={faCircleXmark} className={styles.icon} />
              </div>
              <div
                className={classNames(
                  'col align-items-center d-flex ml-2',
                  styles.text
                )}
              >
                {t('components.content.video.required')}
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        className="d-flex justify-content-center position-relative"
        style={{ height: onMobile ? 'auto' : 465 }}
      >
        <video
          className={styles.video}
          ref={videoRef}
          height="100%"
          controls
          onTimeUpdate={() => {
            const { current } = videoRef
            if (current && !current.seeking) {
              const time = current.currentTime
              setProgressLessonContent(time)
              if (time > maxProgress.current) maxProgress.current = time
            }
          }}
          onSeeking={() => {
            const { current } = videoRef
            if (current) {
              const time = current.currentTime
              const savedTime = videoProgress?.currentTime || 0
              if (lockProgress) {
                const delta = time - savedTime
                const block = time > Math.max(savedTime, maxProgress.current)
                if (delta > 0.01 && block) {
                  current.currentTime = savedTime
                  return
                }
              }
              setProgressLessonContent(current.currentTime)
            }
          }}
          onLoadedMetadata={() => {
            const { current } = videoRef
            if (!videoProgress) return
            const currentTime = videoProgress.currentTime
            if (current && isFinite(currentTime))
              current.currentTime = currentTime
          }}
          aria-label={[
            data.name,
            (!errorPlay && resolution) || 'original',
          ].join(' ')}
          onError={() => setErrorPlay(true)}
          onContextMenu={(e) => {
            if (denyDownload) e.preventDefault()
          }}
          {...(denyDownload && { controlsList: 'nodownload' })}
        >
          <source src={url} type={type} />
          <source src={baseUrl} type={fileData?.metadata.mimeType} />
        </video>
        {loadingProgress && (
          <div className={styles.loading}>
            <Loader color sizes={LoaderSizes.Large} />
          </div>
        )}
      </div>
    </>
  )
}
